import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import Avatar from "@/components/Avatar";
import { toast } from "sonner";
import { X, ArrowLeft, Send, Search, Megaphone, ShieldQuestion, Clock } from "lucide-react";

export default function MessagesPanel({ open, onClose, onRead, initialUser = null }) {
  const [convos, setConvos] = useState([]);
  const [requests, setRequests] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const loadConvos = useCallback(async () => {
    const [c, r] = await Promise.all([api.get("/messages/conversations"), api.get("/messages/requests")]);
    setConvos(c.data);
    setRequests(r.data);
  }, []);

  const loadThread = useCallback(async (uid) => {
    const { data } = await api.get(`/messages/thread/${uid}`);
    setThread(data.messages);
    onRead?.();
  }, [onRead]);

  useEffect(() => { if (open) { loadConvos(); setActive(initialUser || null); setQuery(""); setResults([]); } }, [open, loadConvos, initialUser]);

  useEffect(() => {
    if (!open || !active) return;
    loadThread(active.id);
    const iv = setInterval(() => loadThread(active.id), 5000);
    return () => clearInterval(iv);
  }, [open, active, loadThread]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [thread.length]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const to = setTimeout(async () => {
      const { data } = await api.get(`/members/search?q=${encodeURIComponent(query)}`);
      setResults(data);
    }, 300);
    return () => clearTimeout(to);
  }, [query]);

  const send = async () => {
    if (!text.trim() || !active) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/messages/thread/${active.id}`, { content: text });
      setThread((t) => [...t, data]);
      setText("");
      if (data.pending) toast.info(`Message request sent — ${active.name} will see it once they accept.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const decideRequest = async (r, action) => {
    try {
      await api.post(`/messages/requests/${r.sender_id}/${action}`);
      toast.success(action === "accept" ? `You can now message with ${r.name}` : "Request declined — messages deleted");
      await loadConvos();
      onRead?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[80]" onClick={onClose}>
      <div className="absolute top-16 right-3 w-[min(92vw,380px)] max-h-[460px] bg-pb-surface border border-pb-border shadow-[0_12px_40px_rgba(0,0,0,0.6)] flex flex-col animate-fade-up"
        onClick={(e) => e.stopPropagation()} data-testid="messages-panel">
        <div className="flex items-center gap-3 p-3 border-b border-pb-border shrink-0">
          {active ? (
            <>
              <button data-testid="msg-back-btn" onClick={() => { setActive(null); loadConvos(); }} className="text-zinc-400 hover:text-white"><ArrowLeft className="w-5 h-5" /></button>
              <Avatar src={active.avatar} name={active.name} size={28} />
              <span className="font-display font-black uppercase flex-1 truncate">{active.name}</span>
            </>
          ) : (
            <span className="font-display font-black uppercase text-lg flex-1">Messages</span>
          )}
          <button data-testid="messages-close-btn" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {!active ? (
          <>
            <div className="p-3 border-b border-pb-border shrink-0">
              <div className="relative">
                <Search className="w-4 h-4 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2" />
                <input data-testid="msg-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search members to message…"
                  className="w-full bg-pb-elevated border border-pb-border pl-9 pr-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
              </div>
              {results.length > 0 && (
                <div className="mt-2 border border-pb-border divide-y divide-pb-border/50 max-h-36 overflow-y-auto" data-testid="msg-search-results">
                  {results.map((u) => (
                    <button key={u.id} data-testid={`msg-search-result-${u.id}`} onClick={() => { setActive(u); setQuery(""); setResults([]); }}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-pb-elevated transition-colors text-left">
                      <Avatar src={u.avatar} name={u.name} size={26} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{u.name}</div>
                        {u.location && <div className="text-[10px] text-zinc-500 truncate">{u.location}</div>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="max-h-[264px] overflow-y-auto divide-y divide-pb-border/50" data-testid="conversations-list">
              {requests.length > 0 && (
                <div className="bg-[#F59E0B]/5 border-b border-[#F59E0B]/30" data-testid="message-requests">
                  <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-1 text-[10px] font-black uppercase tracking-[0.2em] text-amber-400">
                    <ShieldQuestion className="w-3.5 h-3.5" /> Message Requests ({requests.length})
                  </div>
                  {requests.map((r) => (
                    <div key={r.sender_id} data-testid={`msg-request-${r.sender_id}`} className="flex items-center gap-2.5 px-3 py-2.5">
                      <Avatar src={r.avatar} name={r.name} size={32} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{r.name}</div>
                        <div className="text-[11px] text-zinc-500 truncate">{r.count} hidden message{r.count === 1 ? "" : "s"} — accept to view</div>
                      </div>
                      <button data-testid={`request-accept-${r.sender_id}`} onClick={() => decideRequest(r, "accept")}
                        className="bg-pb-cyan text-black text-[10px] font-black uppercase tracking-widest px-2.5 py-1.5 hover:bg-[#BEF264] active:scale-95 transition-all">Accept</button>
                      <button data-testid={`request-decline-${r.sender_id}`} onClick={() => decideRequest(r, "decline")}
                        className="border border-pb-border text-zinc-400 text-[10px] font-black uppercase tracking-widest px-2.5 py-1.5 hover:border-red-500 hover:text-red-400 transition-colors">Decline</button>
                    </div>
                  ))}
                </div>
              )}
              {convos.length === 0 && requests.length === 0 && <div className="p-6 text-sm text-zinc-600 text-center">No conversations yet — search a member above to start one.</div>}
              {convos.map((c) => (
                <button key={c.user_id} data-testid={`conversation-${c.user_id}`} onClick={() => setActive({ id: c.user_id, name: c.name, avatar: c.avatar })}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-pb-elevated transition-colors text-left">
                  <Avatar src={c.avatar} name={c.name} size={36} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold flex items-center gap-1.5 truncate">
                      {c.name}
                      {c.is_admin && <Megaphone className="w-3 h-3 text-pb-red shrink-0" />}
                    </div>
                    <div className={`text-xs truncate ${c.unread > 0 ? "text-white font-semibold" : "text-zinc-500"}`}>
                      {c.last_from_me ? "You: " : ""}{c.last}
                    </div>
                  </div>
                  {c.unread > 0 && <span className="bg-pb-red text-black text-[10px] font-black px-1.5 py-0.5 min-w-[18px] text-center" data-testid={`convo-unread-${c.user_id}`}>{c.unread}</span>}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="max-h-[264px] min-h-[160px] overflow-y-auto p-3 space-y-2" data-testid="message-thread">
              {thread.length === 0 && <div className="text-sm text-zinc-600 text-center pt-8">Say hello 👋</div>}
              {thread.map((m) => (
                <div key={m.id} className={`flex ${m.from_me ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                    m.from_me ? "bg-pb-red text-black font-medium" : "bg-pb-elevated border border-pb-border text-zinc-200"}`}>
                    {m.content}
                    {m.pending && <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest mt-1 opacity-70"><Clock className="w-2.5 h-2.5" /> Pending accept</div>}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            <div className="p-3 border-t border-pb-border flex gap-2 shrink-0">
              <input data-testid="msg-input" value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Type a message…"
                className="flex-1 bg-pb-elevated border border-pb-border px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
              <button data-testid="msg-send-btn" onClick={send} disabled={busy || !text.trim()}
                className="bg-pb-red text-black px-4 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-40">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
